/**
 * useSubscription — a tiny generic bridge from a `PortalServices` subscription
 * (unsub-returning callback) to React state. Keeps every page's live-data wiring
 * to one line and guarantees the unsub runs on unmount / dep change.
 */
import { useEffect, useRef, useState } from 'react';
import type { Unsubscribe } from '@djgg/core';

/** Optional per-call options. */
export interface UseSubscriptionOptions {
  /**
   * Called when the underlying listener errors (e.g. a rules PERMISSION_DENIED).
   * Passing this lets a page degrade VISIBLY instead of the SDK logging an
   * uncaught error. Wire it through to the service subscription so the
   * `onSnapshot` error path is handled rather than thrown.
   */
  onError?: (error: unknown) => void;
}

/**
 * Subscribe with `subscribe(cb, onError?)` and expose the latest emitted value.
 * `deps` re-subscribes when they change (e.g. a creation id). Starts at
 * `initial`. When `onError` is given it is forwarded to the subscription so a
 * denied/failed listener surfaces through the handler, not as an uncaught error.
 */
export function useSubscription<T>(
  subscribe: (cb: (value: T) => void, onError?: (error: unknown) => void) => Unsubscribe,
  initial: T,
  deps: unknown[] = [],
  options: UseSubscriptionOptions = {},
): T {
  const [value, setValue] = useState<T>(initial);
  // Keep the latest onError without forcing a re-subscribe when the closure id
  // changes across renders.
  const onErrorRef = useRef(options.onError);
  onErrorRef.current = options.onError;

  useEffect(() => {
    const unsub = subscribe(setValue, (err) => onErrorRef.current?.(err));
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return value;
}
